import { z } from 'zod';
import { zCancelledReason, zOnlinePaymentGateway, zPaymentGateway } from '../enums';

/**
 * The order an event happened in: consumer-web's and consumer-native's cart and checkout events,
 * pos-native's acceptance, and the backend's confirmation, cancellation and payment events. Every
 * one has the `order_uuid`, so all of an order's events can be fetched together.
 */
const orderEventSchema = z.object({
  order_uuid: z.string(),
  /** consumer-native: the signed-in customer. consumer-web doesn't send it: its orders are guests'. */
  customer_id: z.string().optional(),
});

/** The cart item a cart event is about. */
const cartItemEventSchema = orderEventSchema.extend({
  menu_item_uuid: z.string(),
  /** `null` when the item is no longer on the menu. */
  menu_item_name: z.string().nullable(),
  qty: z.number(),
});

/**
 * A customer's call to a venue's AI phone number (backend). `order_uuid` is the call's
 * `orderUuid`, which the order it places takes, so a call's events and its order's are fetched
 * together.
 */
const aiPhoneCallEventSchema = orderEventSchema.extend({
  /** ElevenLabs' `conversation_id`: finds the call's transcript and recording there. */
  conversation_id: z.string(),
});

/** The cart line an AI phone cart event is about, as it is after the change. */
const aiPhoneCartLineEventSchema = aiPhoneCallEventSchema.extend({
  /** The id the agent names the line by: `"1"`, `"2"`, … */
  line_id: z.string(),
  /** The item, as the apps' cart events name it. */
  ...cartItemEventSchema.shape,
  /** The chosen options' names, e.g. `Regular`, `Soy`. */
  options: z.array(z.string()),
  notes: z.string().nullable(),
  /** The cart's total after the change, in cents: what the agent reads out. */
  cart_total: z.number(),
});

/**
 * How the customer chose to pay. consumer-native pays by `CASH`, `SAVED_CARD` or `APPLE_PAY`;
 * `APPLE_PAY` is Square venues only, on the web and in the app on iOS.
 */
const zPaymentType = z.literal(['CASH', 'NEW_CARD', 'SAVED_CARD', 'APPLE_PAY']);

/**
 * Product analytics events sent to PostHog with `track` (`createTracker`). Rules:
 * `posthog/README.md`.
 * - One event name per product action: PostHog insights count and order events by name.
 * - Keys: flat snake_case, so PostHog filters and breakdowns work on them.
 * - Used only as TypeScript types. Never parsed, because tracking must never throw.
 * - Add an event here before sending it.
 */
export const analyticsEventSchemas = {
  /** admin-web: staff submitted the sign-in form. */
  clicked_sign_in: z.object({ signup_method: z.literal(['username', 'OTP', 'SQUARE']) }),
  /** consumer-native: a customer sent feedback from the report issue screen. */
  user_feedback: z.object({
    comments: z.string(),
    customer_id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
  }),
  /** consumer-native: a customer asked to delete their account. */
  delete_account_requested: z.object({ customer_id: z.string() }),
  /** consumer-web, consumer-native: a customer added an item to their cart. */
  cart_item_added: cartItemEventSchema,
  /** consumer-web, consumer-native: a customer saved an item that was already in their cart. */
  cart_item_updated: cartItemEventSchema.extend({ previous_qty: z.number() }),
  /** consumer-web, consumer-native: a customer removed an item from their cart. */
  cart_item_removed: cartItemEventSchema,
  /** consumer-web: a customer emptied their cart with "Clear cart". */
  cart_cleared: orderEventSchema.extend({
    /** Items in the cart, not their quantities. */
    item_count: z.number(),
  }),
  /** consumer-web, consumer-native: a customer tapped an item's quantity stepper, before saving it. */
  item_qty_changed: cartItemEventSchema.extend({ change: z.literal(['increase', 'decrease']) }),
  /** consumer-web, consumer-native: a customer saved an item with notes it didn't open with. */
  item_notes_added: cartItemEventSchema.extend({ notes: z.string() }),
  /**
   * consumer-web, consumer-native: a customer opened checkout (consumer-native's cart screen). Not
   * sent when a card payment returns to it.
   */
  checkout_viewed: orderEventSchema.extend({
    /** Items in the cart, not their quantities. */
    item_count: z.number(),
  }),
  /** consumer-web, consumer-native: a customer tapped "Place order", before anything is checked. */
  place_order_clicked: orderEventSchema.extend({
    /** Cents. */
    order_total: z.number(),
    /** `null` when nothing is payable, which also disables the button. */
    payment_type: zPaymentType.nullable(),
  }),
  /** consumer-web: a customer submitted a form with invalid inputs. */
  form_invalid: orderEventSchema.extend({
    form: z.literal(['customer_details', 'order_item', 'discount_code']),
    invalid_fields: z.array(z.string()),
    error_messages: z.array(z.string()),
  }),
  /** consumer-web, consumer-native: a card payment failed and the customer was shown why. */
  payment_failed: orderEventSchema.extend({
    payment_gateway: zOnlinePaymentGateway.nullable(),
    payment_type: zPaymentType,
    error_message: z.string(),
    /** The bank's 3D Secure check failed, or the customer cancelled it. */
    three_d_secure_failed: z.boolean(),
    /** Stripe's error code, e.g. `card_declined` or `payment_intent_authentication_failure`. */
    stripe_error_code: z.string().optional(),
    stripe_decline_code: z.string().nullable().optional(),
  }),
  /**
   * consumer-web: Worldline sent the customer to their bank for a 3D Secure check. Stripe's checks
   * happen inside its SDK, out of sight: only their failures show, on `payment_failed`.
   */
  three_d_secure_started: orderEventSchema.extend({ payment_gateway: zOnlinePaymentGateway }),
  /** consumer-web: the customer came back from their bank's 3D Secure check. */
  three_d_secure_returned: orderEventSchema.extend({
    payment_gateway: zOnlinePaymentGateway,
    succeeded: z.boolean(),
  }),
  /**
   * consumer-web, consumer-native: after a failed payment, the customer tried again (consumer-web's
   * "Try again", consumer-native's "Place order").
   */
  payment_retried: orderEventSchema,
  /** consumer-web, consumer-native: after a failed payment, the customer switched to cash. */
  payment_switched_to_cash: orderEventSchema,
  /** consumer-web, consumer-native: the order failed to submit, and the customer was told. */
  order_submit_failed: orderEventSchema.extend({
    error_message: z.string(),
    /** The card hold the order was sent with (Stripe's PaymentIntent id). `null` for cash. */
    payment_gateway_payment_id: z.string().nullable(),
  }),
  /** consumer-web, consumer-native: the customer was shown their placed order. */
  order_confirmation_viewed: orderEventSchema.extend({
    /** Cents. */
    order_total: z.number(),
  }),
  /** pos-native: the primary POS accepted an online order, giving the customer a wait time. */
  online_order_accepted: orderEventSchema.extend({
    /** The POS accepted it without staff, as its "Auto accept" setting is on. */
    auto_accepted: z.boolean(),
    /** Minutes. */
    est_wait_time: z.number(),
  }),
  /** backend: an online order was confirmed. A card hold is captured next. */
  order_confirmed: orderEventSchema.extend({
    /** Minutes. */
    est_wait_time: z.number(),
    /** The SMS scheduled to the customer. `null` when none is: only consumer-web orders get one. */
    sms_message: z.string().nullable(),
  }),
  /** backend: an online order was cancelled. A card hold is cancelled next. */
  order_cancelled: orderEventSchema.extend({
    cancelled_reason: zCancelledReason,
    /** The SMS scheduled to the customer. `null` when none is: only consumer-web orders get one. */
    sms_message: z.string().nullable(),
  }),
  /** backend: a confirmed online order's card hold was captured, so the customer is charged. */
  payment_captured: orderEventSchema.extend({
    payment_gateway: zPaymentGateway.nullable(),
    /** Cents. */
    amount: z.number(),
  }),
  /** backend: a cancelled online order's card hold was cancelled, so the customer isn't charged. */
  payment_hold_cancelled: orderEventSchema.extend({
    payment_gateway: zPaymentGateway.nullable(),
    /** Cents. */
    amount: z.number(),
  }),
  /** backend: ElevenLabs connected a call to a venue's AI phone number. */
  ai_phone_call_started: aiPhoneCallEventSchema.extend({
    is_taking_orders: z.boolean(),
    /** Why the venue isn't taking orders, as the agent tells the caller. `null` while it is. */
    not_taking_orders_message: z.string().nullable(),
    /** Caller ID is an Australian mobile, so the confirmation text can go to it unasked. */
    caller_has_mobile: z.boolean(),
  }),
  /** backend: the AI phone agent searched the menu for what the caller asked for. */
  ai_phone_menu_searched: aiPhoneCallEventSchema.extend({
    /** The words the agent searched with. */
    query: z.string(),
    /** How many items matched, past the ones returned too. 0: nothing on the menu matched. */
    result_count: z.number(),
    /** The items returned to the agent, best match first. */
    item_names: z.array(z.string()),
  }),
  /** backend: the AI phone agent added a line to the caller's cart. */
  ai_phone_cart_item_added: aiPhoneCartLineEventSchema,
  /** backend: the AI phone agent changed a line's quantity, options or notes. */
  ai_phone_cart_item_updated: aiPhoneCartLineEventSchema,
  /** backend: the AI phone agent removed a line from the caller's cart. */
  ai_phone_cart_item_removed: aiPhoneCartLineEventSchema,
  /** backend: the AI phone agent tried to place the caller's order. */
  ai_phone_order_submitted: aiPhoneCallEventSchema.extend({
    /** `false` when the cart changed since the agent read it out, so it reads it out again. */
    placed: z.boolean(),
    /** Cents. */
    order_total: z.number(),
    /** Why it wasn't placed, as the agent was told. `null` once placed. */
    error_message: z.string().nullable(),
  }),
  /**
   * backend: an AI phone tool refused the agent's call, e.g. an option the caller still needs to
   * choose, or an item that isn't on the menu. The agent hears `error_message` and asks again.
   */
  ai_phone_tool_failed: aiPhoneCallEventSchema.extend({
    /** The MCP tool, e.g. `add_to_cart`. */
    tool: z.string(),
    /** The tool's arguments as JSON, without `conversationId`. */
    tool_input: z.string(),
    /** What the agent was told, e.g. "Latte needs a choice of Size: …". */
    error_message: z.string(),
  }),
  /** backend: ElevenLabs reported the call over, with its post-call analysis. */
  ai_phone_call_ended: aiPhoneCallEventSchema.extend({
    /** `null` when ElevenLabs didn't report it. */
    duration_secs: z.number().nullable(),
    order_placed: z.boolean(),
    /** 0 to 100: how well the call went for the caller. `null` when ElevenLabs couldn't score it. */
    success_score: z.number().nullable(),
    /** `positive`, `neutral` or `negative`. `null` when the caller said nothing. */
    sentiment: z.string().nullable(),
  }),
};
export type AnalyticsEventName = keyof typeof analyticsEventSchemas;
/** The properties an analytics event is sent with. */
export type AnalyticsEventProperties<E extends AnalyticsEventName> = z.input<
  (typeof analyticsEventSchemas)[E]
>;
